/**
 * Cliente de Supabase para sincronización
 *
 * Maneja la conexión con Supabase para sincronizar
 * datos entre dispositivos (PC y móvil).
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

let supabaseClient: SupabaseClient | null = null;

// Credenciales desde variables de entorno
const SUPABASE_URL = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env['VITE_SUPABASE_ANON_KEY'] as string | undefined;

/**
 * Interfaz para la configuración de Supabase
 */
export interface SupabaseConfig {
  url: string;
  anonKey: string;
}

/**
 * Inicializar el cliente de Supabase con config manual
 */
export function initSupabase(config: SupabaseConfig): SupabaseClient {
  if (supabaseClient) {
    return supabaseClient;
  }

  supabaseClient = createClient(config.url, config.anonKey, {
    auth: {
      persistSession: false, // No necesitamos auth
    },
  });

  return supabaseClient;
}

/**
 * Inicializar Supabase automáticamente desde variables de entorno
 */
export function initSupabaseFromEnv(): SupabaseClient | null {
  if (supabaseClient) {
    return supabaseClient;
  }

  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    console.warn('[Supabase] Variables de entorno no configuradas');
    return null;
  }

  return initSupabase({
    url: SUPABASE_URL,
    anonKey: SUPABASE_ANON_KEY,
  });
}

/**
 * Obtener el cliente de Supabase
 * Retorna null si no está inicializado
 */
export function getSupabase(): SupabaseClient | null {
  return supabaseClient;
}

/**
 * Verificar si Supabase está configurado
 */
export function isSupabaseConfigured(): boolean {
  return supabaseClient !== null;
}
